import React, { useState } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';
import airports from '../data/airports';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const getSuggestions = (input) => {
  if (!input) return [];
  return airports
    .filter(
      (airport) =>
        airport.code.toLowerCase().startsWith(input.toLowerCase()) ||
        airport.name.toLowerCase().includes(input.toLowerCase())
    )
    .slice(0, 50);
};

const renderSuggestionsDropdown = (input, setInput, setShowSuggestions) => {
  const suggestions = getSuggestions(input);
  return (
    suggestions.length > 0 && (
      <ul className="absolute left-0 z-50 w-full bg-white border border-gray-200 rounded-2xl shadow-xl max-h-64 overflow-y-auto mt-1 text-sm">
        {suggestions.map((airport) => (
          <li
            key={airport.code}
            onMouseDown={() => {
              setInput(`${airport.name} (${airport.code})`);
              setShowSuggestions(false);
            }}
            className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 text-[14px]"
          >
            <span className="font-semibold text-gray-900">{airport.name}</span>{' '}
            <span className="text-gray-500">({airport.code})</span>
          </li>
        ))}
      </ul>
    )
  );
};

// Helper: extract IATA code from "Name (XXX)"
const getCode = (str) => {
  if (!str) return '';
  const match = str.match(/\(([^)]+)\)/);
  return match ? match[1] : str;
};

const cabinLabelFromValue = (val) => {
  const map = {
    1: 'Economy',
    2: 'Premium Economy',
    3: 'Business',
    4: 'First',
  };
  return map[val] || 'Economy';
};

const RenderTopFlights = ({
  defaultFrom = '',
  defaultTo = '',
  defaultDepartureDate = '',
  defaultArrivalDate = '',
  defaultTravelers = 1,
  defaultChildren = 0,
  defaultInfants = 0,
  defaultTravelClass = 1,
  defaultTripType = 'Round Trip',
}) => {
  const [tripType, setTripType] = useState(defaultTripType);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [departureDate, setDepartureDate] = useState(defaultDepartureDate);
  const [arrivalDate, setArrivalDate] = useState(defaultArrivalDate);

  const [showTravelerDropdown, setShowTravelerDropdown] = useState(false);
  const [adults, setAdults] = useState(defaultTravelers);
  const [children, setChildren] = useState(defaultChildren);
  const [infants, setInfants] = useState(defaultInfants);
  const [cabinClass, setCabinClass] = useState(defaultTravelClass);

  const [showMobileForm, setShowMobileForm] = useState(false);

  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    const searchId = uuidv4();
    localStorage.setItem('search_id', searchId);

    const formData = {
      from,
      to,
      departureDate,
      arrivalDate,
      adults,
      children,
      infants,
      cabinClass,
      tripType,
    };

    navigate('/flightresults', { state: formData });
  };

  const totalTravellers = adults + children + infants;
  const fromCode = getCode(from) || 'From';
  const toCode = getCode(to) || 'To';
  const cabinLabel = cabinLabelFromValue(cabinClass);

  const renderForm = (isMobile = false) => (
    <form onSubmit={handleSearch}>
      <div
        className={
          isMobile
            ? 'flex flex-col gap-3 mt-4'
            : 'flex flex-wrap md:flex-nowrap gap-3 items-end'
        }
      >
        {/* FROM */}
        <div className={`relative ${isMobile ? 'w-full' : 'w-full md:w-[20%]'}`}>
          <label className="text-[11px] font-semibold text-slate-500 mb-1 block">
            From
          </label>
          <input
            type="text"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setShowFromSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setShowFromSuggestions(false), 150)}
            onFocus={() => setShowFromSuggestions(true)}
            className="h-11 w-full rounded-2xl border border-slate-200 px-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
            placeholder="City or airport"
            required
          />
          {showFromSuggestions &&
            renderSuggestionsDropdown(from, setFrom, setShowFromSuggestions)}
        </div>

        {/* SWAP */}
        {!isMobile && (
          <button
            type="button"
            className="mt-6 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-emerald-500 shadow-sm hover:bg-slate-50"
            onClick={() => {
              const temp = from;
              setFrom(to);
              setTo(temp);
            }}
          >
            <FaExchangeAlt />
          </button>
        )}

        {/* TO */}
        <div className={`relative ${isMobile ? 'w-full' : 'w-full md:w-[20%]'}`}>
          <label className="text-[11px] font-semibold text-slate-500 mb-1 block">
            To
          </label>
          <input
            type="text"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setShowToSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setShowToSuggestions(false), 150)}
            onFocus={() => setShowToSuggestions(true)}
            className="h-11 w-full rounded-2xl border border-slate-200 px-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
            placeholder="City or airport"
            required
          />
          {showToSuggestions &&
            renderSuggestionsDropdown(to, setTo, setShowToSuggestions)}
        </div>

        {/* DEPARTURE */}
        <div className={`${isMobile ? 'w-full' : 'w-full md:w-[16%]'}`}>
          <label className="text-[11px] font-semibold text-slate-500 mb-1 block">
            Departure
          </label>
          <input
            type="date"
            value={departureDate}
            onChange={(e) => setDepartureDate(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-200 px-4 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
            required
          />
        </div>

        {/* RETURN */}
        {tripType === 'Round Trip' && (
          <div className={`${isMobile ? 'w-full' : 'w-full md:w-[16%]'}`}>
            <label className="text-[11px] font-semibold text-slate-500 mb-1 block">
              Return
            </label>
            <input
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              className="h-11 w-full rounded-2xl border border-slate-200 px-4 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
              required
            />
          </div>
        )}

        {/* TRAVELERS & CABIN */}
        <div
          className={`relative ${isMobile ? 'w-full' : 'w-full md:w-[18%]'}`}
        >
          <label className="text-[11px] font-semibold text-slate-500 mb-1 block">
            Travelers &amp; Cabin
          </label>
          <div
            onClick={() => setShowTravelerDropdown(!showTravelerDropdown)}
            className="h-11 w-full cursor-pointer rounded-2xl border border-slate-200 bg-white px-4 text-left text-sm text-slate-800 shadow-sm flex items-center justify-between"
          >
            <span>
              {totalTravellers} Traveler
              {totalTravellers > 1 ? 's' : ''}{' '}
              <span className="text-emerald-500 font-semibold">• {cabinLabel}</span>
            </span>
          </div>

          {showTravelerDropdown && (
            <div className="absolute right-0 mt-2 w-72 rounded-2xl bg-white p-4 shadow-2xl border border-slate-100 z-50">
              {[
                { label: 'Adults (18+)', value: adults, setValue: setAdults, min: 1 },
                { label: 'Children (2-17)', value: children, setValue: setChildren, min: 0 },
                { label: 'Infants (0-2)', value: infants, setValue: setInfants, min: 0 },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between mb-3"
                >
                  <span className="text-sm text-slate-700">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        item.setValue(Math.max(item.value - 1, item.min))
                      }
                      className="h-8 w-8 rounded-md bg-slate-100 hover:bg-slate-200 text-lg leading-none"
                    >
                      –
                    </button>
                    <span className="w-6 text-center text-sm">
                      {item.value}
                    </span>
                    <button
                      type="button"
                      onClick={() => item.setValue(item.value + 1)}
                      className="h-8 w-8 rounded-md bg-slate-100 hover:bg-slate-200 text-lg leading-none"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}

              <select
                className="mt-2 w-full rounded-lg border border-slate-200 p-2 text-sm"
                value={cabinClass}
                onChange={(e) => setCabinClass(Number(e.target.value))}
              >
                <option value={1}>Economy</option>
                <option value={2}>Premium Economy</option>
                <option value={3}>Business</option>
                <option value={4}>First</option>
              </select>
            </div>
          )}
        </div>

        {/* SEARCH BTN */}
        <div className={`${isMobile ? 'w-full mt-2' : 'w-full md:w-[10%]'}`}>
          <button
            type="submit"
            className="h-11 w-full rounded-2xl bg-emerald-500 px-6 text-sm font-semibold text-white shadow-md hover:bg-emerald-600 active:bg-emerald-700 transition"
          >
            Search
          </button>
        </div>
      </div>
    </form>
  );

  return (
    <>
      {/* DESKTOP – pill card like screenshot */}
      <div className="hidden md:block w-full">
        <div className="mx-auto mb-4 w-full max-w-6xl rounded-[32px] bg-white/95 border border-slate-100 shadow-[0_18px_45px_rgba(15,23,42,0.08)] px-6 py-5">
          {/* Trip type toggle */}
          <div className="mb-4 flex items-center gap-3">
            <div className="inline-flex items-center rounded-full bg-slate-100 p-1">
              {['Round Trip', 'One Way'].map((type) => (
                <button
                  key={type}
                  onClick={() => setTripType(type)}
                  type="button"
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition ${
                    tripType === type
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-slate-600'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {renderForm(false)}
        </div>
      </div>

      {/* MOBILE – single summary bar + sheet */}
      <div className="md:hidden px-3 mt-2">
        {/* Summary bar */}
        <button
          type="button"
          onClick={() => setShowMobileForm(true)}
          className="w-full rounded-2xl bg-white border border-slate-100 shadow-md px-4 py-3 flex items-center justify-between"
        >
          <div className="text-left">
            <div className="text-sm font-semibold text-slate-900">
              {fromCode} <span className="text-emerald-500">→</span> {toCode}
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {departureDate || 'Select dates'} • {totalTravellers}{' '}
              Traveller{totalTravellers > 1 ? 's' : ''} • {cabinLabel}
            </div>
          </div>
          <div className="text-[11px] font-semibold text-emerald-600">
            Modify
          </div>
        </button>

        {/* Slide-up sheet */}
        {showMobileForm && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/40"
              onClick={() => setShowMobileForm(false)}
            />
            <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md rounded-l-3xl bg-white shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="text-sm font-semibold text-slate-800">
                  Modify Search
                </div>
                <button
                  onClick={() => setShowMobileForm(false)}
                  className="text-xl leading-none text-slate-500"
                >
                  ×
                </button>
              </div>

              {/* Trip type toggle in sheet */}
              <div className="px-4 pt-3">
                <div className="inline-flex items-center rounded-full bg-slate-100 p-1">
                  {['Round Trip', 'One Way'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setTripType(type)}
                      type="button"
                      className={`px-4 py-1.5 text-xs font-medium rounded-full transition ${
                        tripType === type
                          ? 'bg-emerald-500 text-white shadow-sm'
                          : 'text-slate-600'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-4">
                {renderForm(true)}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default RenderTopFlights;
